/**
 * Hook for quick game predictions
 * Handles prediction state, usage limits, and UI updates
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import PredictionService from '../services/prediction/PredictionService';
import { auth, db } from '../../../firebase';
import { parseMultipleGamePredictions, parseSingleGamePrediction } from '../utils/predictionParser';
import { saveMessage } from '../../../utils/db-messaging';
import { starCoinsClient } from '../../../services/starCoinsClient';
import { 
  addDoc, 
  collection, 
  serverTimestamp, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  Timestamp 
} from 'firebase/firestore';
import CachedPredictionService from '../../../domain/predictions/services/CachedPredictionService';
import { LocalPredictionCache } from '../../../domain/predictions/cache/LocalPredictionCache';

// Maximum games allowed per prediction submission
const MAX_GAMES_PER_PREDICTION = 5;
const LOCAL_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const IS_DEV = process.env.NODE_ENV !== 'production';

const normalizeGameForPrediction = (rawGame) => {
  if (!rawGame) {
    return rawGame;
  }

  const baseTeams = rawGame.teams ? { ...rawGame.teams } : {};
  const homeTeam = baseTeams.home || rawGame.homeTeam || rawGame.home || null;
  const awayTeamCandidate = baseTeams.away || rawGame.awayTeam || rawGame.away || null;
  const visitorsTeamCandidate = baseTeams.visitors || rawGame.visitorsTeam || awayTeamCandidate;

  const normalizedTeams = {
    ...baseTeams,
    home: homeTeam || baseTeams.home || null,
    away: awayTeamCandidate || visitorsTeamCandidate || null,
    visitors: visitorsTeamCandidate || awayTeamCandidate || null
  };

  const fixture = rawGame.fixture?.id
    ? rawGame.fixture
    : (rawGame.id ? { ...(rawGame.fixture || {}), id: rawGame.id } : rawGame.fixture);

  const normalized = {
    ...rawGame,
    fixture,
    teams: normalizedTeams,
    homeTeam: homeTeam || rawGame.homeTeam || null,
    awayTeam: normalizedTeams.away || rawGame.awayTeam || null,
    visitorsTeam: normalizedTeams.visitors || rawGame.visitorsTeam || null,
    predictionMeta: rawGame.predictionMeta
      ? { ...rawGame.predictionMeta }
      : undefined
  };

  if (IS_DEV) {
    console.log('[QuickPrediction] Normalized game for prediction', {
      gameId: normalized.fixture?.id || normalized.id,
      home: normalized.teams?.home?.name,
      away: normalized.teams?.away?.name,
      visitors: normalized.teams?.visitors?.name
    });
  }

  return normalized;
};

export const useQuickPrediction = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [predictions, setPredictions] = useState({});
  const [selectedGames, setSelectedGames] = useState([]);
  const [usageState, setUsageState] = useState({ 
    remaining: 0, 
    canUse: false,
    resetTime: null,
    hoursUntilReset: null,
    isChecking: true  // Add loading state for usage check
  });
  const [error, setError] = useState(null);
  const [showUpgradeNotice, setShowUpgradeNotice] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false); // Additional safety flag
  const [frozenSelections, setFrozenSelections] = useState(new Set()); // Track frozen predictions
  const [activeFilters, setActiveFilters] = useState(null); // Preserve filter state
  const [refundNotice, setRefundNotice] = useState(null); // Show refund notifications
  const cacheServiceRef = useRef(null);

  // Get current user
  const userId = auth.currentUser?.uid;

  // TODO: local cache, the ideal is to move the cache to the backend
  useEffect(() => {
    cacheServiceRef.current = new CachedPredictionService(new LocalPredictionCache(LOCAL_CACHE_TTL_MS), {
      ttlMs: LOCAL_CACHE_TTL_MS
    });
  }, []);

  /**
   * Check for cached predictions within cooldown window
   */
  const checkCachedPredictions = useCallback(async (sport, gameIds) => {
    if (!userId) return {};
    
    try {
      const localCachePreds = await cacheServiceRef.current?.primeFromCache({
        gameIds,
        sport,
        userId
      });
      if (localCachePreds && Object.keys(localCachePreds).length > 0) {
        setPredictions(prev => ({ ...prev, ...localCachePreds }));
        setFrozenSelections(prev => new Set([...prev, ...Object.keys(localCachePreds)]));
      }

      const cachedPredictions = {};
      const now = Date.now();
      const oneHourAgo = Timestamp.fromMillis(now - (60 * 60 * 1000));
      
      // Convert gameIds to strings for consistent comparison
      const gameIdStrings = gameIds.map(id => String(id));
      
      // Query for recent predictions
      const predQuery = query(
        collection(db, 'predictionUsage', userId, 'events'),
        where('eventType', '==', 'prediction_made'),
        where('sport', '==', sport.toUpperCase()),
        where('timestamp', '>', oneHourAgo),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(predQuery);
      console.log(`Found ${querySnapshot.docs.length} recent prediction events for ${sport}`);
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Check if this event has predictions saved and is within cooldown
        if (data.predictions && data.cooldownExpiresAt) {
          const cooldownExpiry = data.cooldownExpiresAt.toMillis();
          if (now < cooldownExpiry) {
            // Map predictions to game IDs
            data.predictions.forEach(pred => {
              const predGameId = String(pred.gameId);
              if (gameIdStrings.includes(predGameId)) {
                console.log(`Found cached prediction for game ${predGameId}`);
              cachedPredictions[predGameId] = {
                ...pred,
                confidenceNumeric: pred.confidenceNumeric || 50.0, // Include numeric confidence with fallback
                cached: true,
                cooldownRemaining: Math.ceil((cooldownExpiry - now) / 1000 / 60)
                };
                // Add to frozen selections (store as string)
                setFrozenSelections(prev => new Set([...prev, predGameId]));
              }
            });
          }
        }
      });
      
      // Automatically update predictions state with cached predictions
      if (Object.keys(cachedPredictions).length > 0) {
        setPredictions(prev => ({
          ...prev,
          ...cachedPredictions
        }));
        // Persist Firestore-backed cached predictions locally using remaining cooldown
        const minRemaining = Math.min(
          ...Object.values(cachedPredictions).map(p => p.cooldownRemaining || 60)
        );
        await cacheServiceRef.current?.saveToCache(cachedPredictions, {
          sport,
          userId,
          expiresAtMs: now + (Math.max(minRemaining, 1) * 60 * 1000)
        });
        console.log(`Loaded ${Object.keys(cachedPredictions).length} cached predictions into state`);
      }
      
      return cachedPredictions;
    } catch (error) {
      console.error('Error checking cached predictions:', error);
      return {};
    }
  }, [userId]);

  // Check star coins balance on mount
  useEffect(() => {
    const checkUsage = async () => {
      if (userId) {
        // For quick predictions, we only care about star coins balance
        const balance = await starCoinsClient.loadBalance();
        const canUse = balance >= 7; // Need at least 7 coins for 1 game
        
        setUsageState({
          remaining: Math.floor(balance / 7), // How many games they can predict
          canUse: canUse,
          starCoinsBalance: balance,
          resetTime: null, // Not applicable for star coins
          hoursUntilReset: null,
          isChecking: false
        });
      } else {
        // If no user, mark as done checking but keep canUse as false
        setUsageState(prev => ({
          ...prev,
          isChecking: false
        }));
      }
    };
    checkUsage();
  }, [userId]);

  /**
   * Toggle game selection
   */
  const toggleGameSelection = useCallback((game) => {
    const normalizedGame = normalizeGameForPrediction(game);
    const gameId = String(normalizedGame?.fixture?.id || normalizedGame?.id);
    
    // Prevent toggling frozen predictions
    if (frozenSelections.has(gameId)) {
      console.log('Game prediction is frozen for cooldown period');
      return;
    }
    
    // Prevent toggling while loading
    if (isLoading || isButtonDisabled) {
      return;
    }
    
    setSelectedGames(prev => {
      const isSelected = prev.some(g => String(g.fixture?.id || g.id) === gameId);
      
      if (isSelected) {
        // Clear any selection limit error when deselecting
        setError(null);
        return prev.filter(g => String(g.fixture?.id || g.id) !== gameId);
      } else {
        // Check if already at max selections
        if (prev.length >= MAX_GAMES_PER_PREDICTION) {
          setError(`Maximum ${MAX_GAMES_PER_PREDICTION} games per prediction. Submit these first, then select more.`);
          return prev; // Don't add the game
        }
        
        // Check if user can make predictions when adding a selection
        // Only show upgrade notice if we've finished checking usage and user can't use predictions
        if (!usageState.isChecking && !usageState.canUse && prev.length === 0) {
          // Show upgrade notice when first game is selected and user is out of predictions
          setShowUpgradeNotice(true);
        }
        
        // Clear any previous error
        setError(null);
        return [...prev, normalizedGame];
      }
    });
  }, [usageState.canUse, usageState.isChecking, isLoading, isButtonDisabled, frozenSelections]);

  /**
   * Clear all selections
   * Note: This only clears the selected games, NOT the predictions
   * Predictions should persist so they remain visible in the cards
   */
  const clearSelections = useCallback(() => {
    // Prevent clearing while loading
    if (isLoading || isButtonDisabled) {
      return;
    }
    setSelectedGames([]);
    // Don't clear predictions - they should persist in the UI
    // setPredictions({}); // REMOVED - predictions should stay visible
    setError(null);
  }, [isLoading, isButtonDisabled]);

  /**
   * Store current filter state (to preserve during prediction)
   */
  const preserveFilterState = useCallback((filters) => {
    setActiveFilters(filters);
  }, []);

  /**
   * Predict selected games
   */
  const predictSelectedGames = useCallback(async (sport = 'football') => {
    // Double-check to prevent multiple simultaneous requests
    if (isLoading || isButtonDisabled) {
      console.log('Prediction already in progress, ignoring click');
      return;
    }
    
    if (!userId) {
      setError('Please sign in to use predictions');
      return;
    }

    if (selectedGames.length === 0) {
      setError('Please select at least one game');
      return;
    }

    if (selectedGames.length > MAX_GAMES_PER_PREDICTION) {
      setError(`Maximum ${MAX_GAMES_PER_PREDICTION} games per prediction`);
      return;
    }

    // Check for cached predictions first
    const gameIds = selectedGames.map(g => String(g.fixture?.id || g.id));
    const cachedPreds = await checkCachedPredictions(sport, gameIds);
    
    // Filter out games that already have cached predictions
    const gamesNeedingPrediction = selectedGames.filter(game => {
      const gameId = String(game.fixture?.id || game.id);
      return !cachedPreds[gameId];
    });
    
    // If all games have cached predictions, just return them
    if (gamesNeedingPrediction.length === 0 && Object.keys(cachedPreds).length > 0) {
      setPredictions(prev => ({ ...prev, ...cachedPreds }));
      setError('Using cached predictions (1-hour cooldown active)');
      return;
    }
    
    // Only check star coins if we need new predictions
    if (gamesNeedingPrediction.length > 0 && !usageState.canUse) {
      // Show upgrade notice when trying to predict without available uses
      setShowUpgradeNotice(true);
      return;
    }

    // Set both loading states immediately to prevent double-clicks
    setIsLoading(true);
    setIsButtonDisabled(true);
    setError(null);
    setRefundNotice(null); // Clear any previous refund notices

    try {
      // Only process and charge for games needing new predictions
      const gamesToPredict = gamesNeedingPrediction.length > 0 ? gamesNeedingPrediction : selectedGames;
      
      // Calculate cost: 7 coins per game (only for new predictions)
      const totalCost = gamesToPredict.length * 7;
      
      // If we have some cached predictions, let user know
      if (Object.keys(cachedPreds).length > 0) {
        console.log(`Using ${Object.keys(cachedPreds).length} cached predictions, making ${gamesToPredict.length} new predictions`);
      }
      
      // Only charge if we're making new predictions
      let paymentSuccess = true;
      if (gamesToPredict.length > 0) {
        console.log('Charging', totalCost, 'Star Coins for', gamesToPredict.length, 'new predictions');
        const result = await starCoinsClient.consumeStarCoins(totalCost, 'aiSlotSpin');
        paymentSuccess = result.success;
      
        if (!paymentSuccess) {
          // Check if user has enough balance
          const userBalance = await starCoinsClient.loadBalance();
          if (userBalance < totalCost) {
            setError(`Need ${totalCost} Star Coins (you have ${userBalance}). Each game costs 7 Star Coins.`);
            setShowUpgradeNotice(true);
          } else {
            setError(result.error || `Failed to process Star Coins payment`);
          }
          setIsLoading(false);
          return;
        }
        console.log('Successfully deducted', totalCost, 'Star Coins');
      }

      // Store initial tracking data - will save after getting predictions
      const trackingData = {
        userId,
        sport: sport.toUpperCase(),
        gamesCount: selectedGames.length,
        starCoinsUsed: totalCost,
        games: selectedGames.map(g => ({
          id: g.fixture?.id || g.id,
          home: g.teams?.home?.name || g.home?.name,
          away: g.teams?.away?.name || g.away?.name
        })),
        platform: window.innerWidth <= 768 ? 'mobile' : 'desktop',
        tier: localStorage.getItem('userTier') || 'basic'
      };

      // Update star coins balance after successful payment
      const newBalance = await starCoinsClient.loadBalance();
      setUsageState({
        remaining: Math.floor(newBalance / 7),
        canUse: newBalance >= 7,
        starCoinsBalance: newBalance,
        resetTime: null,
        hoursUntilReset: null,
        isChecking: false
      });

      // Make predictions only for games that need them
      let predictionResult = { success: true };
      let newPredictions = {};
      
      if (gamesToPredict.length > 0) {
        if (gamesToPredict.length === 1) {
          predictionResult = await PredictionService.predictSingleGame({
            game: gamesToPredict[0],
            sport: sport,
            userId: userId
          });
          if (predictionResult.success) {
            const parsed = parseSingleGamePrediction(predictionResult.prediction);
            const gameId = String(gamesToPredict[0].fixture?.id || gamesToPredict[0].id);
            newPredictions[gameId] = parsed;
          }
        } else {
          predictionResult = await PredictionService.predictMultipleGames({
            games: gamesToPredict,
            sport: sport,
            userId: userId
          });
          if (predictionResult.success) {
            newPredictions = parseMultipleGamePredictions(predictionResult.predictions, gamesToPredict);
          }
        }
      }

      if (predictionResult.success) {
        // Filter out N/A predictions (failed predictions)
        const validNewPredictions = {};
        const failedPredictions = [];
        
        Object.entries(newPredictions).forEach(([gameId, pred]) => {
          // Check if this is a failed/N/A prediction
          // The parser now sets winner to 'N/A' for failed predictions
          const isFailedPrediction = 
            pred.confidence === 'N/A' || 
            pred.winner === 'N/A' ||
            !pred.confidence ||
            !pred.winner ||
            (pred.winner === 'See analysis' && pred.confidence === 'N/A');
          
          if (isFailedPrediction) {
            failedPredictions.push(gameId);
            console.log(`Game ${gameId} failed to get a valid prediction (winner: ${pred.winner}, confidence: ${pred.confidence})`);
          } else {
            validNewPredictions[gameId] = pred;
          }
        });
        
        // Refund star coins for failed predictions
        if (failedPredictions.length > 0) {
          const refundAmount = failedPredictions.length * 7;
          console.log(`Refunding ${refundAmount} Star Coins for ${failedPredictions.length} failed predictions`);
          
          try {
            // Add star coins back to user's balance
            await starCoinsClient.addStarCoins(refundAmount, 'prediction_refund');
            console.log(`Successfully refunded ${refundAmount} Star Coins`);
            
            // Set refund notification message
            const validCount = Object.keys(validNewPredictions).length;
            if (validCount === 0) {
              // All predictions failed
              setRefundNotice(`No coins used - unable to predict ${failedPredictions.length} game${failedPredictions.length > 1 ? 's' : ''}`);
            } else {
              // Some predictions succeeded, some failed
              setRefundNotice(`${refundAmount} coins refunded for ${failedPredictions.length} unavailable prediction${failedPredictions.length > 1 ? 's' : ''}`);
            }
            
            // Clear the refund notice after 5 seconds
            setTimeout(() => {
              setRefundNotice(null);
            }, 5000);
            
            // Update usage state with new balance
            const newBalance = await starCoinsClient.loadBalance();
            setUsageState({
              remaining: Math.floor(newBalance / 7),
              canUse: newBalance >= 7,
              starCoinsBalance: newBalance,
              resetTime: null,
              hoursUntilReset: null,
              isChecking: false
            });
          } catch (refundError) {
            console.error('Failed to refund Star Coins:', refundError);
          }
        } else if (Object.keys(validNewPredictions).length > 0) {
          // All predictions succeeded - show success notice
          const successCost = Object.keys(validNewPredictions).length * 7;
          setRefundNotice(`✓ ${successCost} coins used for ${Object.keys(validNewPredictions).length} prediction${Object.keys(validNewPredictions).length > 1 ? 's' : ''}`);
          setTimeout(() => {
            setRefundNotice(null);
          }, 3000);
        }
        
        // Combine cached and valid new predictions
        const allValidPredictions = {
          ...cachedPreds,
          ...validNewPredictions
        };
        
        // Update state with all predictions (including N/A ones for display)
        const allPredictions = {
          ...cachedPreds,
          ...newPredictions
        };
        setPredictions(prev => ({
          ...prev,
          ...allPredictions
        }));

        // Save predictions (cached + new) locally for instant rehydration
        await cacheServiceRef.current?.saveToCache(allValidPredictions, {
          sport,
          userId
        });
        
        // Only freeze games with VALID predictions (not N/A)
        const validPredictedGameIds = Object.keys(allValidPredictions).map(id => String(id));
        setFrozenSelections(prev => new Set([...prev, ...validPredictedGameIds]));
        
        // Only save VALID NEW predictions to Firestore (not cached or N/A ones)
        if (Object.keys(validNewPredictions).length > 0) {
          try {
            const now = Timestamp.now();
            const cooldownExpiry = Timestamp.fromMillis(
              now.toMillis() + (60 * 60 * 1000) // 1 hour cooldown
            );
            
            // Create sport-game IDs for valid predictions only
            const validGamesForFirestore = gamesToPredict.filter(g => {
              const gameId = String(g.fixture?.id || g.id);
              return validNewPredictions[gameId];
            });
            
            const sportGameIds = validGamesForFirestore.map(g => {
              const gameId = String(g.fixture?.id || g.id);
              return `${sport.toUpperCase()}_${gameId}`;
            });
            
            // Calculate actual cost (after refunds)
            const actualCost = Object.keys(validNewPredictions).length * 7;
            
            // Save to predictionUsage with extended fields
            const userPredictionRef = collection(db, 'predictionUsage', userId, 'events');
            await addDoc(userPredictionRef, {
              userId,
              eventType: 'prediction_made',
              feature: 'quick_prediction',
              sport: sport.toUpperCase(),
              gamesCount: Object.keys(validNewPredictions).length,
              starCoinsUsed: actualCost,
              failedPredictions: failedPredictions.length,
              timestamp: serverTimestamp(),
              
              // New fields for caching and reuse
              sportGameIds,
              predictions: Object.entries(validNewPredictions).map(([gameId, pred]) => ({
                gameId: String(gameId),
                sportGameId: `${sport.toUpperCase()}_${gameId}`,
                winner: pred.winner,
                confidence: pred.confidence,
                confidenceNumeric: pred.confidenceNumeric || 50.0, // Default to 50 if not present
                reason: pred.reason,
                full: pred.full
              })),
              predictionState: 1, // Active
              cooldownExpiresAt: cooldownExpiry,
              predictionGeneratedAt: now,
              
              metadata: {
                games: validGamesForFirestore.map(g => {
                  // Extract timestamp/date based on sport structure
                  let timestamp = null;  // Unix timestamp in milliseconds
                  let date = null;       // Date string

                  // Log raw game data to debug missing timestamps
                  console.log(`[useQuickPrediction] Processing game for metadata:`, {
                    gameId: g.fixture?.id || g.id,
                    sport: sport.toUpperCase(),
                    hasFixture: !!g.fixture,
                    fixtureTimestamp: g.fixture?.timestamp,
                    fixtureDate: g.fixture?.date,
                    rawTimestamp: g.timestamp,
                    teams: {
                      home: g.teams?.home?.name || g.home?.name,
                      away: g.teams?.away?.name || g.away?.name
                    }
                  });

                  // Football/Soccer structure
                  if (g.fixture?.timestamp) {
                    timestamp = g.fixture.timestamp * 1000; // Convert seconds to milliseconds
                    date = g.fixture.date;
                    console.log(`[useQuickPrediction] Found football timestamp: ${timestamp} (${new Date(timestamp).toISOString()})`);
                  }
                  // NFL/NCAAF structure
                  else if (g.game?.date?.date && g.game?.date?.time) {
                    const dateStr = `${g.game.date.date}T${g.game.date.time}`;
                    date = dateStr;
                    timestamp = new Date(dateStr).getTime();
                    console.log(`[useQuickPrediction] Found NFL/NCAAF timestamp: ${timestamp}`);
                  }
                  // NBA structure
                  else if (g.date?.start) {
                    date = g.date.start;
                    timestamp = new Date(g.date.start).getTime();
                    console.log(`[useQuickPrediction] Found NBA timestamp: ${timestamp}`);
                  }
                  // MLB/Baseball structure
                  else if (g.date) {
                    date = g.date;
                    timestamp = new Date(g.date).getTime();
                    console.log(`[useQuickPrediction] Found MLB timestamp: ${timestamp}`);
                  }
                  // MMA structure
                  else if (g.event_date) {
                    date = g.event_date;
                    timestamp = new Date(g.event_date).getTime();
                    console.log(`[useQuickPrediction] Found MMA timestamp: ${timestamp}`);
                  }
                  // Generic fallback
                  else if (g.timestamp) {
                    timestamp = g.timestamp;
                    // If timestamp is in seconds, convert to milliseconds
                    if (timestamp < 10000000000) {
                      timestamp = timestamp * 1000;
                    }
                    console.log(`[useQuickPrediction] Found generic timestamp: ${timestamp}`);
                  } else {
                    console.warn(`[useQuickPrediction] NO TIMESTAMP FOUND for game ${g.fixture?.id || g.id}`);
                  }

                  return {
                    id: g.fixture?.id || g.id,
                    home: g.teams?.home?.name || g.home?.name,
                    away: g.teams?.away?.name || g.teams?.visitors?.name || g.away?.name,
                    timestamp,  // Always in milliseconds for consistency
                    date        // Original date string from API
                  };
                }),
                dashboard: trackingData.platform,
                tier: trackingData.tier,
                maxGamesLimit: MAX_GAMES_PER_PREDICTION,
                cachedUsed: Object.keys(cachedPreds).length, // Track how many cached predictions were used
                totalRequested: selectedGames.length,
                failedPredictions: failedPredictions.length
              }
            });
            
            console.log(`Saved ${Object.keys(validNewPredictions).length} valid predictions with cache data`);
          } catch (trackingError) {
            console.error('Error saving prediction:', trackingError);
          }
        }
        
        // Save ALL predictions to chat history (cached + new)
        try {
          const predictionSummary = formatPredictionsForHistory(selectedGames, allPredictions, sport);
          await saveMessage(userId, {
            text: predictionSummary,
            sender: 'assistant'
          });
        } catch (saveError) {
          console.error('Failed to save predictions to history:', saveError);
          // Don't show error to user - saving to history is not critical
        }
      } else {
        setError(predictionResult.error || 'Prediction failed');
      }

    } catch (err) {
      console.error('Prediction error:', err);
      setError('Failed to get prediction. Please try again.');
    } finally {
      setIsLoading(false);
      // Add a small delay before re-enabling to prevent rapid clicks
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 500);
    }
  }, [selectedGames, userId, usageState.canUse, usageState.resetTime, isLoading, isButtonDisabled]);

  /**
   * Get prediction for a specific game
   */
  const getGamePrediction = useCallback((gameId) => {
    const prediction = predictions[String(gameId)];
    if (!prediction) return null;

    // Add visual indicator if this is a cached prediction
    if (prediction.cached) {
      const numericDisplay = prediction.confidenceNumeric ?
        `${prediction.confidenceNumeric}% ` : '';
      return {
        ...prediction,
        displayText: `${prediction.winner} (${numericDisplay}${prediction.confidence}) [Cached - ${prediction.cooldownRemaining}m remaining]`
      };
    }
    return prediction;
  }, [predictions]);

  /**
   * Check if a game is selected
   */
  const isGameSelected = useCallback((game) => {
    const gameId = String(game.fixture?.id || game.id);
    return selectedGames.some(g => String(g.fixture?.id || g.id) === gameId);
  }, [selectedGames]);

  /**
   * Format predictions for saving to chat history
   */
  const formatPredictionsForHistory = (games, predictions, sport) => {
    const date = new Date().toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/New_York'
    });

    let summary = `⚡ Quick ${sport.charAt(0).toUpperCase() + sport.slice(1)} Predictions - ${date}\n\n`;

    games.forEach((game, index) => {
      const gameId = String(game.fixture?.id || game.id);
      const prediction = predictions[gameId];
      const homeTeam = game.teams?.home?.name || game.home?.name || 'Home';
      const awayTeam = game.teams?.away?.name || game.teams?.visitors?.name || game.away?.name || 'Away';
      
      if (prediction) {
        summary += `**${index + 1}. ${awayTeam} @ ${homeTeam}**\n`;
        summary += `   Winner: ${prediction.winner || 'N/A'}\n`;
        // Include numerical confidence if available
        const confidenceDisplay = prediction.confidenceNumeric ?
          `${prediction.confidenceNumeric}% (${prediction.confidence || 'N/A'})` :
          (prediction.confidence || 'N/A');
        summary += `   Confidence: ${confidenceDisplay}\n`;
        if (prediction.reason) {
          summary += `   Reason: ${prediction.reason}\n`;
        }
        summary += '\n';
      }
    });
    
    summary += `_Generated ${games.length} prediction${games.length > 1 ? 's' : ''} using Quick Picks_`;
    
    return summary;
  };

  return {
    // State
    isLoading,
    predictions,
    selectedGames,
    usageState,
    error,
    showUpgradeNotice,
    refundNotice, // NEW: Notification for refunds
    isButtonDisabled, // Export this for UI components to use
    frozenSelections, // Games in cooldown period
    activeFilters, // Preserved filter state
    
    // Actions
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    setShowUpgradeNotice,
    preserveFilterState, // Save current filters
    checkCachedPredictions, // Check for cached predictions
    
    // Helpers
    getGamePrediction,
    isGameSelected,
    hasSelections: selectedGames.length > 0,
    selectionCount: selectedGames.length,
    isGameFrozen: (gameId) => frozenSelections.has(String(gameId))
  };
};
